import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type { ChangePasswordRequest, ChangePasswordResponse } from '@nadoutong/contracts';
import { changePassword as apiChangePassword } from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { TechnicalDetails } from '../components/TechnicalDetails.tsx';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, PageHeader, Panel } from '../ui/index.ts';
import { MembersCard } from './settings/MembersCard.tsx';

/**
 * `/settings` — account info, technical ids, org members, and the
 * in-session password change (epic #950 PR-4, local Field Notes rebuild
 * of the console-shared `createSettingsPage` factory instantiation:
 * sections account → technical → members → security, members shown when
 * the caller holds `org:members:read`).
 *
 * Password change calls `POST /v1/auth/password/change`; a 401
 * INVALID_CREDENTIALS envelope renders as an inline "current password
 * incorrect" field error, INVALID_PAYLOAD surfaces the server message
 * on the new-password field. Length is validated locally and on the
 * server (10–128 chars).
 */

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;

export function Settings(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  const showMembers = hasPermission(user, 'org:members:read');

  return (
    <section data-testid="settings-page" className="flex flex-col gap-4">
      <PageHeader title={t('settings.title')} sub={t('settings.subtitle')} testId="settings-header" />
      <AccountCard />
      <TechnicalCard />
      {showMembers ? <MembersCard /> : null}
      <ChangePasswordCard />
    </section>
  );
}

function AccountCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  return (
    <Panel caption={t('settings.account.title')} padded testId="settings-account-card">
      <div className="flex flex-col gap-4">
        <p className="m-0 text-caption text-ink-low">{t('settings.account.description')}</p>
        <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-label text-ink-mid">{t('settings.account.email')}</dt>
            <dd className="m-0 font-mono text-body text-ink" data-testid="settings-account-email">
              {user?.email ?? '—'}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-label text-ink-mid">{t('settings.account.email_verified')}</dt>
            <dd className="m-0" data-testid="settings-email-verified">
              {user?.emailVerified === true ? (
                <StatusChip variant="ok" label={t('settings.account.status.verified')} />
              ) : (
                <StatusChip variant="warning" label={t('settings.account.status.unverified')} />
              )}
            </dd>
          </div>
        </dl>
      </div>
    </Panel>
  );
}

function TechnicalCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  return (
    <TechnicalDetails summary={t('settings.technical.summary')} testId="settings-technical-card">
      <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <dt className="text-label text-ink-mid">{t('settings.account.account_id')}</dt>
          <dd className="m-0 font-mono text-body text-ink-mid" data-testid="settings-account-id">
            {user?.accountId ?? '—'}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-label text-ink-mid">{t('settings.account.org_id')}</dt>
          <dd className="m-0 font-mono text-body text-ink-mid" data-testid="settings-org-id">
            {user?.orgId ?? '—'}
          </dd>
        </div>
      </dl>
    </TechnicalDetails>
  );
}

interface FieldErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

function useChangePasswordMutation(): UseMutationResult<
  ChangePasswordResponse,
  Error,
  ChangePasswordRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => apiChangePassword(body, fetchImpl),
  });
}

function ChangePasswordCard(): JSX.Element {
  const t = useT();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const mutation = useChangePasswordMutation();

  function resetForm(): void {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setToast(null);

    const nextErrors: FieldErrors = {};
    if (currentPassword.length === 0) {
      nextErrors.currentPassword = t('settings.security.error.current_required');
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_short', {
        min: String(MIN_PASSWORD_LENGTH),
      });
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_long', {
        max: String(MAX_PASSWORD_LENGTH),
      });
    }
    if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('settings.security.error.mismatch');
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    try {
      await mutation.mutateAsync({ currentPassword, newPassword });
      setToast(t('settings.security.toast.success'));
      resetForm();
    } catch (err) {
      if (err instanceof PlatformApiError) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setFieldErrors({
            currentPassword: t('settings.security.error.current_incorrect'),
          });
          return;
        }
        if (err.code === 'INVALID_PAYLOAD') {
          setFieldErrors({ newPassword: err.message });
          return;
        }
        setFormError(`${err.code}: ${err.message}`);
        return;
      }
      setFormError(t('settings.security.error.network'));
    }
  }

  const submitting = mutation.isPending;

  return (
    <Panel caption={t('settings.security.title')} padded testId="settings-security-card">
      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={handleSubmit}
        data-testid="settings-change-password-form"
        noValidate
      >
        <span className="text-label text-forest">{t('settings.security.change_password')}</span>
        <FormField
          label={t('settings.security.current_password')}
          fieldId="settings-current-password"
          error={
            fieldErrors.currentPassword !== undefined ? (
              <span data-testid="settings-current-password-error">
                {fieldErrors.currentPassword}
              </span>
            ) : undefined
          }
        >
          <Input
            id="settings-current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            invalid={fieldErrors.currentPassword !== undefined}
            data-testid="settings-current-password"
          />
        </FormField>
        <FormField
          label={t('settings.security.new_password')}
          fieldId="settings-new-password"
          error={
            fieldErrors.newPassword !== undefined ? (
              <span data-testid="settings-new-password-error">{fieldErrors.newPassword}</span>
            ) : undefined
          }
        >
          <Input
            id="settings-new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            invalid={fieldErrors.newPassword !== undefined}
            data-testid="settings-new-password"
          />
        </FormField>
        <FormField
          label={t('settings.security.confirm_password')}
          fieldId="settings-confirm-password"
          error={
            fieldErrors.confirmPassword !== undefined ? (
              <span data-testid="settings-confirm-password-error">
                {fieldErrors.confirmPassword}
              </span>
            ) : undefined
          }
        >
          <Input
            id="settings-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            invalid={fieldErrors.confirmPassword !== undefined}
            data-testid="settings-confirm-password"
          />
        </FormField>
        {formError !== null ? (
          <p
            role="alert"
            className="m-0 text-label text-bad"
            data-testid="settings-change-password-error"
          >
            {formError}
          </p>
        ) : null}
        {toast !== null ? (
          <p
            role="status"
            className="m-0 rounded-lg border border-ok-line bg-ok-tint px-4 py-2 text-label text-ink"
            data-testid="settings-change-password-toast"
          >
            {toast}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="settings-change-password-submit"
          >
            {submitting ? t('settings.security.submitting') : t('settings.security.submit')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
